import { FormEvent, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { Alert, Button, Card, Form, Spinner } from "react-bootstrap"
import { useAdminUsersFetch } from "../../../hooks/fetch/admin/users/useAdminUsersFetch"
import { useRankingFetch } from "../../../hooks/fetch/useRankingFetch"
import useConfig from "../../../hooks/config/useConfig"
import { RankingSearchUserType } from "../../../interfaces/ranking"

const SEARCH_DELAY = 400
const MAX_RESULTS = 8

export const ChangeUserPassword = () => {

    const { changeUserPasswordService } = useAdminUsersFetch()
    const { findUsersRanking } = useRankingFetch()
    const { allCountries } = useConfig()
    const [ userSearch , setUserSearch ] = useState('')
    const [ userResults , setUserResults ] = useState<RankingSearchUserType[]>([])
    const [ searchingUsers , setSearchingUsers ] = useState(false)
    const [ selectedUser , setSelectedUser ] = useState<RankingSearchUserType | null>(null)
    const [ password , setPassword ] = useState('')
    const [ repeatPassword , setRepeatPassword ] = useState('')
    const [ showPassword , setShowPassword ] = useState(false)
    const [ submitting , setSubmitting ] = useState(false)
    const [ result , setResult ] = useState<{ success : boolean , message : string } | null>(null)

    // Misma búsqueda que el admin de Ranking (POST /api/ranking/find-user)
    const userTimer = useRef<any>(0)
    const searchUsers = (term : string) => {
        clearTimeout(userTimer.current)
        setUserSearch(term)
        if (term.trim().length < 2) {
            setUserResults([])
            return
        }
        userTimer.current = setTimeout(() => {
            setSearchingUsers(true)
            findUsersRanking(term.trim()).then(res => {
                setSearchingUsers(false)
                if (res.success && res.data) {
                    const merged = [
                        ...(res.data.by_real_estate || []),
                        ...(res.data.by_name || []),
                        ...(res.data.by_email || [])
                    ]
                    const unique = merged.filter((u, i, arr) => arr.findIndex(x => x.id == u.id) == i)
                    setUserResults(unique.slice(0, MAX_RESULTS))
                } else {
                    setUserResults([])
                }
            }).catch(err => {
                setSearchingUsers(false)
                console.error(err)
            })
        }, SEARCH_DELAY)
    }

    const pickUser = (user : RankingSearchUserType | null) => {
        setSelectedUser(user)
        setResult(null)
        setPassword('')
        setRepeatPassword('')
    }

    const countryName = (countryID : string) => allCountries.find(c => c.identifier == countryID)?.name

    const handleSubmit = async (e : FormEvent) => {
        e.preventDefault()
        setResult(null)

        if(!selectedUser){
            setResult({ success : false , message : 'Elegí un usuario' })
            return
        }
        if(password.trim().length < 8){
            setResult({ success : false , message : 'La contraseña debe tener al menos 8 caracteres' })
            return
        }
        if(password !== repeatPassword){
            setResult({ success : false , message : 'Las contraseñas no coinciden' })
            return
        }

        setSubmitting(true)
        changeUserPasswordService({ user_id : selectedUser.id , password })
        .then((data) => {
            if(data.success){
                setResult({ success : true , message : `Se actualizó la contraseña de ${data.data}.` })
                setPassword('')
                setRepeatPassword('')
            }else{
                setResult({ success : false , message : data.message || 'Ocurrió un error' })
            }
        })
        .catch(() => setResult({ success : false , message : 'Ocurrió un error' }))
        .finally(() => setSubmitting(false))
    }

    return (
        <div className="p-5 change-user-password">
            <div className="mb-4 py-2 px-4 d-flex justify-content-between flex-wrap position-relative">
                <Link to="/admin" className="fs-2 go-back mt-3 ms-3 ms-md-4">
                    <i className="text-light bi bi-arrow-left"></i>
                </Link>
                <h3>CAMBIAR CONTRASEÑA DE USUARIO</h3>
                <div style={{ width: 150 }}></div>
            </div>

            <Card className="mx-auto" style={{ maxWidth: 560 }}>
                <Card.Body className="p-4">
                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Usuario *</Form.Label>
                            {
                                !selectedUser &&
                                <Form.Control
                                    placeholder="Buscar por inmobiliaria, nombre o email..."
                                    value={userSearch}
                                    onChange={(ev) => searchUsers(ev.target.value)}
                                    autoFocus
                                />
                            }
                        </Form.Group>

                        {!selectedUser && searchingUsers && <small className="text-muted d-block mb-3">Buscando...</small>}

                        {!selectedUser && userResults.length > 0 &&
                            <div className="list-group mb-4">
                                {userResults.map(user => (
                                    <button
                                        type="button"
                                        className="list-group-item list-group-item-action"
                                        key={user.id}
                                        onClick={() => pickUser(user)}
                                    >
                                        <span className="fw-500">{user.name}</span>
                                        {user.real_estate && <span className="text-muted"> · {user.real_estate}</span>}
                                        <small className="text-muted d-block">{user.email}</small>
                                    </button>
                                ))}
                            </div>
                        }

                        {!selectedUser && userSearch.trim().length > 1 && !searchingUsers && userResults.length == 0 &&
                            <small className="text-muted d-block mb-3">Sin resultados</small>
                        }

                        {
                            selectedUser &&
                            <div className="selected-user mb-4">
                                <span className="selected-user-avatar">
                                    <i className="bi bi-person"></i>
                                </span>
                                <div className="flex-grow-1 overflow-hidden">
                                    <strong className="d-block text-truncate">{selectedUser.name}</strong>
                                    <div className="text-muted small text-truncate">{selectedUser.email}</div>
                                    <div className="small mt-1 d-flex flex-wrap gap-3">
                                        {selectedUser.real_estate && <span><i className="bi bi-building me-1"></i>{selectedUser.real_estate}</span>}
                                        {countryName(selectedUser.countryID) && <span><i className="bi bi-geo-alt me-1"></i>{countryName(selectedUser.countryID)}</span>}
                                        <span className="text-muted">ID {selectedUser.id}</span>
                                    </div>
                                </div>
                                <button type="button" className="btn btn-link text-muted p-0 align-self-start" onClick={() => pickUser(null)} title="Cambiar usuario">
                                    <i className="bi bi-x-lg"></i>
                                </button>
                            </div>
                        }

                        <fieldset disabled={!selectedUser}>
                            <Form.Group className="mb-3">
                                <Form.Label>Nueva contraseña *</Form.Label>
                                <Form.Control
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoComplete="new-password"
                                    required
                                />
                                <Form.Text className="text-muted">
                                    Mínimo 8 caracteres.
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Repetir contraseña *</Form.Label>
                                <Form.Control
                                    type={showPassword ? "text" : "password"}
                                    value={repeatPassword}
                                    onChange={(e) => setRepeatPassword(e.target.value)}
                                    autoComplete="new-password"
                                    required
                                />
                            </Form.Group>

                            <Form.Check
                                className="mb-4"
                                type="checkbox"
                                id="show-password"
                                label="Mostrar contraseña"
                                checked={showPassword}
                                onChange={(e) => setShowPassword(e.target.checked)}
                            />
                        </fieldset>

                        {
                            result &&
                            <Alert variant={result.success ? "success" : "danger"}>
                                {result.message}
                            </Alert>
                        }

                        <div className="d-flex justify-content-end gap-2">
                            <Link to="/admin">
                                <Button variant="outline-secondary" disabled={submitting}>
                                    Cancelar
                                </Button>
                            </Link>
                            <Button type="submit" variant="primary" disabled={submitting || !selectedUser}>
                                {submitting ? (
                                    <>
                                        <Spinner size="sm" animation="border" className="me-2" />
                                        Guardando...
                                    </>
                                ) : 'Cambiar contraseña'}
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </div>
    )
}
